import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, TextField, MenuItem, FormControlLabel, Switch } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InputAdornment from "@mui/material/InputAdornment";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import "../../assets/scss/tables.scss";

/* The kinds of thing an IT team issues. Kept here rather than in the database
   so the list can change without a migration. */
export const ASSET_CATEGORIES = [
    "Laptop",
    "MacBook Laptop",
    "External Monitor",
    "Mouse",
    "Mobile Device",
    "Keyboard",
    "Tablet",
    "Software License",
    "Other"
];

/* Asked for only when the asset is a laptop. */
export const PROCESSORS = ["i3", "i5", "i7"];
export const RAM_SIZES = ["8GB", "16GB", "32GB"];

const THIS_YEAR = new Date().getFullYear();
export const MODEL_YEARS = Array.from({ length: 12 }, (_, i) => String(THIS_YEAR - i));

/* the category whose extra fields appear below */
const LAPTOP = "Laptop";

const BLANK = {
    ItemName: "",
    category: "",
    make: "",
    model: "",
    description: "",
    isReturnable: true,
    totalStock: "",
    modelYear: "",
    processor: "",
    ram: ""
};

const AssetFormModal = ({ show, asset, onClose, onSave }) => {

    const [form, setForm] = useState(BLANK);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const isEdit = Boolean(asset && asset._id);

    /* refill whenever the popup opens, so a cancelled edit leaves nothing behind */
    useEffect(() => {
        if (!show) return;
        setForm(asset ? { ...BLANK, ...asset } : BLANK);
        setTouched(false);
        setSaving(false);
    }, [show, asset]);

    const set = (field) => (event) => {
        let value = field === "isReturnable" ? event.target.checked : event.target.value;
        /* stock is a whole number of things; strip anything else as it is typed */
        if (field === "totalStock") value = value.replace(/[^0-9]/g, "");

        setForm((prev) => {
            const next = { ...prev, [field]: value };
            /* a mouse has no processor — drop the laptop fields when the
               category moves away, so they cannot be saved by accident */
            if (field === "category" && value !== LAPTOP) {
                next.modelYear = "";
                next.processor = "";
                next.ram = "";
            }
            return next;
        });
    };

    const isLaptop = form.category === LAPTOP;

    /* how many are already out, so stock cannot be set below what people hold */
    const issued = Number(asset?.issued || 0);
    const stockTooLow = isEdit && form.totalStock !== "" && Number(form.totalStock) < issued;

    const nameMissing = !form.ItemName.trim();

    const submit = async (event) => {
        event.preventDefault();
        setTouched(true);
        if (nameMissing || stockTooLow || saving) return;

        setSaving(true);
        const ok = await onSave(form, isEdit);
        setSaving(false);
        if (ok) onClose();
    };

    return (
        <Modal show={show} onHide={onClose} backdrop="static" className="wh-modal">
            <form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? "Edit Asset" : "Add New Asset"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="wh-form-grid">

                        <div className="wh-form-full">
                            <TextField
                                label="Asset Name"
                                placeholder="Laptop, Access Card, Docking Station…"
                                value={form.ItemName}
                                onChange={set("ItemName")}
                                error={touched && nameMissing}
                                helperText={touched && nameMissing ? "An asset name is required" : " "}
                                size="small"
                                fullWidth
                                autoFocus
                                required
                            />
                        </div>

                        <TextField
                            select
                            label="Category"
                            value={form.category}
                            onChange={set("category")}
                            size="small"
                            fullWidth
                            helperText=" "
                        >
                            {ASSET_CATEGORIES.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Making Company"
                            placeholder="Apple, Dell, Logitech…"
                            value={form.make}
                            onChange={set("make")}
                            size="small"
                            fullWidth
                            helperText=" "
                        />

                        <TextField
                            label="Stock Quantity"
                            placeholder="0"
                            value={form.totalStock}
                            onChange={set("totalStock")}
                            error={stockTooLow}
                            helperText={stockTooLow
                                ? `${issued} already issued`
                                : "How many the company owns"}
                            size="small"
                            fullWidth
                            inputProps={{ inputMode: "numeric", maxLength: 6 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">
                                    <Inventory2OutlinedIcon fontSize="small" />
                                </InputAdornment>
                            }}
                        />

                        <div className="wh-form-full mt-2">
                            <TextField
                                label="Model"
                                placeholder="MacBook Pro 14, UltraSharp U2723QE…"
                                value={form.model}
                                onChange={set("model")}
                                size="small"
                                fullWidth
                                helperText=" "
                            />
                        </div>

                        {isLaptop && (
                            <>
                                <div className="wh-form-full">
                                    <h6 className="wh-section-title"><MemoryOutlinedIcon />Laptop specification</h6>
                                </div>

                                <TextField
                                    select
                                    label="Model Year"
                                    value={form.modelYear}
                                    onChange={set("modelYear")}
                                    size="small"
                                    fullWidth
                                    helperText=" "
                                >
                                    {MODEL_YEARS.map((year) => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    label="Processor"
                                    value={form.processor}
                                    onChange={set("processor")}
                                    size="small"
                                    fullWidth
                                    helperText=" "
                                >
                                    {PROCESSORS.map((cpu) => (
                                        <MenuItem key={cpu} value={cpu}>{cpu}</MenuItem>
                                    ))}
                                </TextField>

                                <div className="wh-form-full">
                                    <TextField
                                        select
                                        label="RAM"
                                        value={form.ram}
                                        onChange={set("ram")}
                                        size="small"
                                        fullWidth
                                        helperText=" "
                                    >
                                        {RAM_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>{size}</MenuItem>
                                        ))}
                                    </TextField>
                                </div>
                            </>
                        )}

                        <div className="wh-form-full">
                            <TextField
                                label="Description"
                                placeholder="Anything the IT team should know when issuing this"
                                value={form.description}
                                onChange={set("description")}
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </div>

                        <div className="wh-form-full">
                            <div className="wh-switch-row">
                                <FormControlLabel
                                    control={<Switch checked={Boolean(form.isReturnable)} onChange={set("isReturnable")} />}
                                    label="Must be returned when the employee leaves"
                                />
                                <p className="wh-switch-hint">
                                    <Inventory2OutlinedIcon />
                                    Turn this off for items an employee keeps, such as a headset or a licence.
                                </p>
                            </div>
                        </div>

                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={saving || stockTooLow}>
                        {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Asset"}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default AssetFormModal;
